// WishDetailActionBar — the mobile docked bar on `/wishes/:id`.
//
// ## This component, twice
//
// **W49.0 P2-3** replaced a hand-rolled `fixed bottom-0` +
// `bg-paper-elevated` surface with the app's `glass-1 hairline-t` tokens. The
// reasoning in the original header — "the material is now the app's, not this
// file's" — was true, and then stopped being true.
//
// **W51 D-6 (`ADR-0164`)** moved every *other* bar off that band: the action
// bar stopped being a full-bleed warm-glass strip and became a floating navy
// pill in the nav's own material, so that the two stacked objects on screen
// are the same thing. This component did not follow, and nothing said so —
// until the user pointed at this screen by name. For the whole of W51 the
// wish detail bar was the one place where content scrolling past it looked
// measurably different from the diary, calendar and create screens.
//
// So the header above used to claim a shared material that no longer existed.
// That is the same failure `dockedBarClaim` had one sprint earlier: a comment
// recording the defect as a fact, plus a test pinning the old material, both
// green.
//
// ## Why it still is not `PageBottomActionBar`
//
// The primitive's `primary` is **required**, and this bar has a second state:
// when the wish is finished or abandoned there is no CTA, only a status line.
// Expressing that as a primary would mean inventing a disabled button to stand
// in for a state that has no action. So the chrome is shared (`.action-pill`,
// written to match `.nav-pill` property for property and guarded by
// `action-bar-navy-material.test.ts`) and the contents stay local.
//
// **Two menu defects went with it.** All three overflow entries rendered
// `MoreHorizontal`, so the menu offered three identical glyphs and nothing to
// tell them apart by; each entry now has its own icon. And 「分享心願」 was
// `onClick={() => undefined}` — a button that looked real and did nothing.
// It is removed rather than disabled: `AGENTS.md §3 #4` bans placeholders, and
// `ShareSheet` (Phase 2) is not wired. A dead affordance costs more than a
// missing one.
//
// The overflow trigger stays a button here rather than moving into
// `PageBottomActionBar`'s `secondary` slot: that slot is icon-only buttons in a
// fixed position, and this is a labelled menu with two entries.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, LayoutPanelTop, NotebookPen, MoreHorizontal, type LucideIcon } from "lucide-react";
import {
  gapDown,
  gapUp,
  POPOVER_PANEL_CLASS,
  POPOVER_ROW_CLASS,
  POPOVER_TILE_CLASS,
  usePopoverPlacement,
} from "@/components/system/Popover";

export interface WishDetailActionBarProps {
  wishId: string;
  /** Whether the wish can be marked complete (status pending|in_progress). */
  canComplete: boolean;
  /** Whether the bar should render at all (parent already decides viewport). */
  show: boolean;
  /** Callback to open the CompletionDialog (BottomSheet on mobile per W34.3.0). */
  onMarkComplete: () => void;
}

const MENU_LABELS = {
  editLayout: "編輯 layout",
  linkedDiaries: "連結的日記",
} as const;

// W49.0 P2-3 — one icon per entry. They all used to be `MoreHorizontal`.
const MENU_ICONS: Record<keyof typeof MENU_LABELS, LucideIcon> = {
  editLayout: LayoutPanelTop,
  linkedDiaries: NotebookPen,
};

interface OverflowMenuProps {
  open: boolean;
  onClose: () => void;
  items: { key: keyof typeof MENU_LABELS; label: string; onClick: () => void; icon: LucideIcon }[];
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

function OverflowMenu({ open, onClose, items, anchorRef }: OverflowMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  // W52.3 — the shared rule, replacing the hand-picked `right-0` / `left-0`.
  // This is the component that showed why it matters: W52.2 moved the trigger
  // from the right end of the bar to the left and left the anchor behind, and
  // two thirds of a 180px panel went off a 390px screen. The rule reads the
  // trigger's actual position instead of trusting whoever wrote the class.
  const placement = usePopoverPlacement(anchorRef, "top");

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      onClose();
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return (
    <div
      ref={menuRef}
      data-component="WishDetailActionBarMenu"
      data-testid="wish-detail-action-bar-menu"
      role="menu"
      data-align={placement.align}
      data-side={placement.side}
      // W52.4 — the gap follows the side (`marginBottom` opens upward), and the
      // edge below follows it too. The trigger is inside a bar pinned to the
      // bottom of the screen, so the rule says "up"; it is read rather than
      // assumed because the same bar is a `position: fixed` element that a
      // future layout change could move.
      style={{
        ...placement.style,
        ...(placement.side === "top" ? gapUp() : gapDown()),
      }}
      className={[
        POPOVER_PANEL_CLASS,
        "absolute z-30 min-w-[180px]",
        /* gap via inline style — see `gapUp` */
        placement.side === "top" ? "bottom-full origin-bottom" : "top-full origin-top",
      ].join(" ")}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            onClick={() => {
              onClose();
              item.onClick();
            }}
            data-testid={`wish-detail-action-bar-menu-${item.key}`}
            className={POPOVER_ROW_CLASS}
          >
            <span className={POPOVER_TILE_CLASS}>
              <Icon size={17} aria-hidden />
            </span>
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The surface is `PageBottomActionBar`'s (`.action-pill`); the contents are
 * not, for the reason in the file header — there is a state with no CTA, and
 * the primitive's `primary` is required. `action-bar-navy-material.test.ts`
 * compares `.action-pill` against `.nav-pill` property by property and fails
 * if either side is tuned alone, which is what keeps the two from drifting
 * apart again.
 */
export function WishDetailActionBar({
  wishId,
  canComplete,
  show,
  onMarkComplete,
}: WishDetailActionBarProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  if (!show) return null;

  const items: OverflowMenuProps["items"] = [
    {
      key: "editLayout",
      label: MENU_LABELS.editLayout,
      icon: MENU_ICONS.editLayout,
      onClick: () => navigate(`/wishes/${wishId}/edit-layout`),
    },
    {
      key: "linkedDiaries",
      label: MENU_LABELS.linkedDiaries,
      icon: MENU_ICONS.linkedDiaries,
      onClick: () => navigate(`/wishes/${wishId}?diaries=1#linked-diaries`),
    },
  ];

  return (
    // `.action-pill` — the floating navy pill from `globals.css`, written to
    // match `.nav-pill` property for property. Carrying the class rather than
    // the values is what makes `action-bar-navy-material.test.ts` able to
    // compare the two classes instead of comparing them to literals.
    //
    // `z-20` not `z-30`: the PillNav is z-30. This page calls `useHidePillNav()`
    // (`WishDetail.tsx:87`), so they never coexist — but matching the primitive
    // means one page cannot reintroduce the two-bar strip W42 §7.9 removed.
    <div
      data-component="WishDetailActionBar"
      data-testid="wish-detail-action-bar"
      data-can-complete={canComplete ? "true" : "false"}
      className={[
        "action-pill",
        "fixed z-20",
        // W52.4 — the claimed-edge lift used to be spelled out here a second
        // time (`bottom-[env(safe-area-inset-bottom)] md:bottom-0`, "identical to
        // PageBottomActionBar's branch"). It was not identical in effect — it
        // rendered at **0px** against the nav's 10px — and it was a second
        // implementation of a value that belongs to `.action-pill`, which is the
        // whole shape W52.2 got fixed for: this bar sat on the old full-bleed
        // glass for the whole of W51 while every bar around it moved to the pill,
        // because no guard's set of "the bars" was derived from what a bar *is*.
        //
        // The slot now comes from the class, so a second copy of the number
        // cannot exist here to drift. This page always claims the edge
        // (`WishDetail.tsx`), so it is the `data-nav-visible="false"` case.
      ].join(" ")}
    >
      {/*
        `w-full` is load-bearing, same reason as in `PageBottomActionBar`:
        `.action-pill` is `display: flex; justify-content: center`, so this row
        is sized by its **content** and centred — measured there as 144px of
        contents inside a 298px pill with the CTA squeezed to zero padding.
        Nothing about a flex item grows by default.
      */}
      <div className="flex w-full items-center gap-2 px-1.5">
        <div className="relative">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="更多操作"
            data-testid="wish-detail-action-bar-overflow"
            // The nav's own secondary idiom: white/10 on navy. `bg-paper-deep`
            // would be near-white on near-black.
            className="tap-target press inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/15 border border-white/15 transition-colors duration-quick ease-paper"
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden />
          </button>
          <OverflowMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            items={items}
            anchorRef={triggerRef}
          />
        </div>
        {canComplete ? (
          <button
            type="button"
            onClick={onMarkComplete}
            data-testid="wish-detail-action-bar-mark-complete"
            // **lilac + ink-1**, the same pair `PageBottomActionBar`'s primary
            // uses and the same one `.nav-item-on` spends: ink-1 on lilac is
            // 6.32:1, where white-on-lilac is 2.69:1 (fails AA) and
            // you-strong-on-navy is 2.89:1 (below the 3:1 floor for UI
            // boundaries). Changing this colour by eye puts one of those
            // failures back.
            //
            // It was `bg-ink-1 text-paper` — near-black on near-black, i.e. a
            // shape that reads as a hole in the bar rather than a control.
            // `min-w-11` is not decoration. The CTA is `flex-1`, so its width is
            // whatever the pill leaves over — which is fine at 298px and is
            // exactly the case where a guard has nothing to measure, because a
            // fluid flex item resolves to no width class at all. The floor
            // states the guarantee in the DOM instead of leaving it implicit.
            className="tap-target press flex-1 h-11 min-w-11 inline-flex items-center justify-center gap-2 rounded-pill bg-surface-lilac text-ink-1 font-semibold px-4 transition-transform duration-quick ease-quiet"
          >
            <Check className="h-4 w-4" aria-hidden />
            標為完成
          </button>
        ) : (
          <div
            data-testid="wish-detail-action-bar-status"
            // `.action-pill` sets `color: rgb(255 255 255 / 0.62)`; the status
            // line inherits it and needs no colour of its own.
            className="flex-1 h-11 flex items-center justify-center px-2 text-body-sm"
          >
            此心願已完成或放棄
          </div>
        )}
      </div>
    </div>
  );
}